import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { styles, Modal } from '../theme';

import { API_URL, fetchWithAuth } from '../api';
import { usePermissions } from '../context/PermissionContext';
import { formatCurrency } from '../utils/formatUtils';

const emptyForm = { name: '', discount_type: 'percentage', value: '', is_active: 1 };

const PosDiscounts = ({ token }) => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [discounts, setDiscounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchDiscounts(currentPage, perPage, searchQuery);
  }, [currentPage, perPage, searchQuery]);

  const fetchDiscounts = async (page = 1, limit = 10, search = '') => {
    try {
      const queryParams = new URLSearchParams({
        page,
        per_page: limit,
        search: search
      });

      const res = await fetchWithAuth(`${API_URL}/pos/discounts?${queryParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();

      setDiscounts(data.data);
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);
    } catch (err) {
      console.error('Error fetching discounts:', err);
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.discount_type) {
      alert('Missing required fields (Name, Type)');
      return;
    }

    try {
      const url = editingId ? `${API_URL}/pos/discounts/${editingId}` : `${API_URL}/pos/discounts`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, value: form.value === '' ? null : form.value })
      });
      if (res.ok) {
        fetchDiscounts(currentPage, perPage, searchQuery);
        setShowForm(false);
        setEditingId(null);
        setForm(emptyForm);
      } else {
        const errData = await res.json();
        alert('Error: ' + (errData.error || JSON.stringify(errData.error)));
      }
    } catch (err) {
      console.error('Error saving discount:', err);
    }
  };

  const handleEdit = (discount) => {
    setForm({
      name: discount.name || '',
      discount_type: discount.discount_type || 'percentage',
      value: discount.value === null || discount.value === undefined ? '' : discount.value,
      is_active: discount.is_active === undefined ? 1 : discount.is_active
    });
    setEditingId(discount.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this discount?')) return;
    try {
      await fetchWithAuth(`${API_URL}/pos/discounts/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchDiscounts(currentPage, perPage, searchQuery);
    } catch (err) {
      console.error('Error deleting discount:', err);
    }
  };

  const formatValue = (discount) => {
    if (discount.value === null || discount.value === undefined) return 'Enter at sale';
    return discount.discount_type === 'percentage' ? `${discount.value}%` : `₱${formatCurrency(discount.value)}`;
  };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Discounts</h1>
        {canCreate('pos_discounts') && (
          <button onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); }} style={styles.button}>
            <Plus style={{ width: '20px', height: '20px' }} /> Add Discount
          </button>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={`${editingId ? 'Edit' : 'Add'} Discount`}
        maxWidth="500px"
      >
        <div style={styles.formGroup}>
          <label style={styles.label}>Name *</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={styles.input} />
        </div>
        <div style={styles.grid2}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Type *</label>
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} style={styles.input}>
              <option value="percentage">% (Percentage)</option>
              <option value="summation">Summation (Flat Amount)</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Value</label>
            <input
              type="number"
              step="0.01"
              placeholder="Leave blank to enter at sale"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              style={styles.input}
            />
          </div>
        </div>
        {editingId && (
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                style={{ marginRight: '8px' }}
              />
              Active
            </label>
          </div>
        )}
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowForm(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          <button onClick={handleSubmit} style={styles.button}>Save</button>
        </div>
      </Modal>

      <div style={{ ...styles.card, marginBottom: '20px' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Search</label>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            style={styles.input}
          />
        </div>
      </div>

      <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Value</th>
                <th style={styles.th}>Status</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {discounts.map(d => (
                <tr key={d.id}>
                  <td style={styles.td}>{d.name}</td>
                  <td style={styles.td}>{d.discount_type === 'percentage' ? '%' : 'Summation'}</td>
                  <td style={styles.td}>{formatValue(d)}</td>
                  <td style={styles.td}>
                    <span style={{ color: d.is_active ? '#059669' : '#b91c1c' }}>
                      {d.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    {canUpdate('pos_discounts') && (
                      <button onClick={() => handleEdit(d)} style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}>
                        <Edit size={18} />
                      </button>
                    )}
                    {canDelete('pos_discounts') && (
                      <button onClick={() => handleDelete(d.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                        <Trash2 size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Showing {totalCount === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalCount)} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} entries
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>Rows per page:</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '14px'
              }}
            >
              {[5, 10, 25, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronLeft size={16} />
            </button>

            <div style={{ display: 'flex', gap: '4px', margin: '0 8px' }}>
              {[...Array(totalPages || 0)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: currentPage === pageNum ? '#4f46e5' : 'white',
                        color: currentPage === pageNum ? 'white' : '#374151',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === 2 && currentPage > 3) ||
                  (pageNum === totalPages - 1 && currentPage < totalPages - 2)
                ) {
                  return <span key={pageNum} style={{ padding: '6px' }}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PosDiscounts;
